import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { usePublicPaths } from '@/hooks/usePublicPaths'
import { useUserType } from '@/hooks/useUserType'
import { LoadingScreen } from '@/components/auth/LoadingScreen'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
}

/**
 * ProtectedRoute — requires Better Auth session via AuthProvider.
 * Admin profile lookup only runs when requireAdmin is set.
 */
const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth()
  const location = useLocation()
  const isPublicPath = usePublicPaths()
  const isCurrentPathPublic = isPublicPath(location.pathname)
  const { userType, isLoading: userTypeLoading, error: userTypeError } = useUserType()

  if (isCurrentPathPublic) {
    return <>{children}</>
  }

  if (authLoading) {
    return <LoadingScreen />
  }

  if (!user) {
    return <Navigate to="/signin" replace state={{ returnTo: location.pathname }} />
  }

  // Profile/admin type is only required for admin-gated routes.
  // Waiting on /api/profiles/me for every protected page caused contact-approval
  // (and others) to hang when the profile request raced ahead of the session.
  if (requireAdmin) {
    if (userTypeLoading) {
      return <LoadingScreen />
    }
    if (userTypeError) {
      console.error('Error checking user type:', userTypeError)
      return <Navigate to="/dashboard" replace />
    }
    if (userType !== 'admin') {
      return <Navigate to="/dashboard" replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
